// /v2 navigation groups. Five primary, four secondary.
//
// A group is visible only when its own VITE_<SLUG>_V2_ENABLED flag
// is exactly 'true'. Children are not top-level items. Workflow
// definitions and System config use viewerIsSuperAdmin, the same
// check as the unprefixed sidebar. Nothing here reads an advertiser
// id, a workspace id, or localStorage.

import { viewerIsSuperAdmin } from '../../routes';
import { isBrandV2Enabled } from '../../pages/BrandV2/flag';
import { isCatalogV2Enabled } from '../../pages/CatalogV2/flag';
import { isConnectionsV2Enabled } from '../../pages/ConnectionsV2/flag';
import { isDecisionsV2Enabled } from '../../pages/DecisionsV2/flag';
import { isFinanceV2Enabled } from '../../pages/FinanceV2/flag';
import { isHomeV2Enabled } from '../../pages/HomeV2/flag';
import { performanceV2Enabled } from '../../pages/PerformanceV2/performanceFlag';
import { isPlansV2Enabled } from '../../pages/PlansV2/flag';
import { isRunsV2Enabled } from '../../pages/RunsV2/flag';
import { isSettingsV2Enabled } from '../../pages/SettingsV2/flag';
import { CATALOG_LIBRARY_PATH, isLibraryV2Enabled, isVersionsV2Enabled, PLANS_VERSIONS_PATH } from './flag';

export type NavAuth = {
  status: string;
  user?: { isSuperAdmin?: boolean };
};

export type NavIaChild = {
  id: string;
  label: string;
  to: string;
  testId: string;
};

export type NavIaItem = {
  id: string;
  label: string;
  description: string;
  to: string;
  testId: string;
  children: NavIaChild[];
};

export type NavIaTree = {
  primary: NavIaItem[];
  secondary: NavIaItem[];
};

type Gate = (auth: NavAuth) => boolean;

type ChildSpec = {
  id: string;
  label: string;
  to: string;
  when: Gate;
};

type ItemSpec = {
  id: string;
  label: string;
  description: string;
  to: string;
  group: 'primary' | 'secondary';
  when: Gate;
  children?: ChildSpec[];
};

const ITEMS: readonly ItemSpec[] = [
  {
    id: 'home',
    label: 'Home',
    description: 'Login briefing',
    to: '/v2/home',
    group: 'primary',
    when: () => isHomeV2Enabled(),
  },
  {
    id: 'plans',
    label: 'Plans',
    description: 'Plan list and drafts',
    to: '/v2/plans',
    group: 'primary',
    when: () => isPlansV2Enabled(),
    children: [
      {
        id: 'versions',
        label: 'Versions',
        to: PLANS_VERSIONS_PATH,
        when: () => isVersionsV2Enabled(),
      },
    ],
  },
  {
    id: 'catalog',
    label: 'Catalog',
    description: 'Products and creative library',
    to: '/v2/catalog',
    group: 'primary',
    when: () => isCatalogV2Enabled(),
    children: [
      {
        id: 'library',
        label: 'Creative Library',
        to: CATALOG_LIBRARY_PATH,
        when: () => isLibraryV2Enabled(),
      },
    ],
  },
  {
    id: 'performance',
    label: 'Performance',
    description: 'Snapshots',
    to: '/v2/performance',
    group: 'primary',
    when: () => performanceV2Enabled(),
  },
  {
    id: 'decisions',
    label: 'Decisions',
    description: 'Approvals inbox',
    to: '/v2/decisions',
    group: 'primary',
    when: () => isDecisionsV2Enabled(),
  },
  {
    id: 'runs',
    label: 'Runs',
    description: 'Production runs and workflows',
    to: '/v2/runs',
    group: 'secondary',
    when: () => isRunsV2Enabled(),
    children: [
      {
        id: 'workflow-definitions',
        label: 'Workflow definitions',
        to: '/v2/runs?tab=definitions',
        when: (auth) => viewerIsSuperAdmin(auth),
      },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    description: 'Ledger, rate card, and exports',
    to: '/v2/finance',
    group: 'secondary',
    when: () => isFinanceV2Enabled(),
  },
  {
    id: 'brand',
    label: 'Brand',
    description: 'Profile, audiences, and rights',
    to: '/v2/brand',
    group: 'secondary',
    when: () => isBrandV2Enabled(),
  },
  {
    id: 'settings',
    label: 'Settings',
    description: 'Connections, team, and account',
    to: '/v2/settings',
    group: 'secondary',
    when: () => isSettingsV2Enabled(),
    children: [
      {
        id: 'connections',
        label: 'Connections',
        to: '/v2/connections',
        when: () => isConnectionsV2Enabled(),
      },
      {
        id: 'system-config',
        label: 'System config',
        to: '/v2/settings?tab=system-config',
        when: (auth) => viewerIsSuperAdmin(auth),
      },
    ],
  },
];

function toItem(spec: ItemSpec, auth: NavAuth): NavIaItem {
  const children = (spec.children ?? [])
    .filter((child) => child.when(auth))
    .map((child) => ({
      id: child.id,
      label: child.label,
      to: child.to,
      testId: `nav-ia-v2-child-${child.id}`,
    }));
  return {
    id: spec.id,
    label: spec.label,
    description: spec.description,
    to: spec.to,
    testId: `nav-ia-v2-item-${spec.id}`,
    children,
  };
}

export function buildNavIa(auth: NavAuth): NavIaTree {
  const visible = ITEMS.filter((item) => item.when(auth));
  return {
    primary: visible.filter((item) => item.group === 'primary').map((item) => toItem(item, auth)),
    secondary: visible.filter((item) => item.group === 'secondary').map((item) => toItem(item, auth)),
  };
}
